const UNAVAILABLE_COPY = {
  expired: {
    title: "Outside the pairing window",
    message: "Pairings are shown only 1–7 days before expiration, not after an item expires.",
  },
  outside_window: {
    title: "Pairings start at 7 days",
    message: "This item is not yet within the 1–7 day pairing window.",
  },
  inactive: {
    title: "Batch is inactive",
    message: "Pairings are available only for active inventory batches.",
  },
  depleted: {
    title: "No remaining stock",
    message: "Pairings are available only while this batch has quantity remaining.",
  },
  invalid_date: {
    title: "Expiration date needed",
    message: "Enter a valid expiration date to check for pairings in the 1–7 day window.",
  },
  catalog_unavailable: {
    title: "Product unavailable",
    message: "This product could not be verified in your company catalog.",
  },
};

export function isPairingUrgencyEligible(urgency) {
  return urgency === "urgent" || urgency === "attention";
}

function insidePairingWindow(data) {
  const days = data?.days_to_expiry;
  return Number.isInteger(days) && days >= 1 && days <= 7;
}

/** Never present stale, generic, policy, expired, or unavailable cards as pairings. */
export function pairingCards(data) {
  if (!insidePairingWindow(data)
      || data?.eligible === false
      || data?.expired === true
      || data?.degraded === true
      || data?.pairing_status === "profile_review_required"
      || data?.pairing_status === "no_matches"
      || data?.unavailable_reason) return [];
  if (!Array.isArray(data?.cards)) return [];
  return data.cards.filter((card) => card?.kind === "combo"
    && typeof card.title === "string" && card.title.trim()
    && typeof card.action === "string" && card.action.trim()
    && Array.isArray(card.items) && card.items.length >= 2
    && card.items.every((item) => typeof item?.product_id === "string" && item.product_id.trim()
      && typeof item.name === "string" && item.name.trim()
      && typeof item.role === "string" && item.role.trim()
      && (item.source === "inventory" || item.source === "catalog"))
    && new Set(card.items.map((item) => item.product_id)).size === card.items.length);
}

export function pairingDisplayName(data, batch) {
  if (typeof data?.anchor?.name === "string" && data.anchor.name.trim()) return data.anchor.name;
  if (typeof batch?.product_name === "string" && batch.product_name.trim()) return batch.product_name;
  return "Selected product";
}

export function pairingWindowText(data) {
  const days = data?.days_to_expiry;
  if (!Number.isInteger(days)) return null;
  if (days >= 1 && days <= 7) {
    return `${days} ${days === 1 ? "day" : "days"} until expiry · Pairing window: days 1–7`;
  }
  if (days <= 0) return "Expired · Pairings are shown 1–7 days before expiry";
  return `${days} days until expiry · Pairings begin at 7 days`;
}

export function pairingEmptyCopy(data) {
  if (data?.degraded) {
    return {
      title: "Pairings temporarily unavailable",
      message: "We couldn’t verify pairings for this item. Please try again.",
    };
  }
  if (!insidePairingWindow(data) || data?.eligible === false || data?.expired === true || data?.unavailable_reason) {
    const days = data?.days_to_expiry;
    const inferredReason = !Number.isInteger(days) ? "invalid_date"
      : days <= 0 || data?.expired === true ? "expired"
        : days > 7 ? "outside_window" : null;
    return UNAVAILABLE_COPY[data?.unavailable_reason || inferredReason] || {
      title: "Outside the pairing window",
      message: "Specific pairings are available only 1–7 days before expiration.",
    };
  }
  if (data?.pairing_status === "profile_review_required") {
    return {
      title: "Pairing profile needed",
      message: "This product needs an approved pairing profile before specific matches can be recommended.",
    };
  }
  if (data?.pairing_status === "no_matches") {
    return {
      title: "No catalog pairings found",
      message: "No verified complementary products match this item in your accessible company catalog.",
    };
  }
  return {
    title: "No verified pairings available",
    message: "ShelfLife did not find a specific product match for this item.",
  };
}
